import React, { useMemo, useState } from 'react';
import {
    Box, Chip, Collapse,
    IconButton, Paper, Stack, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow,
    Tooltip, Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import moment from 'moment';
import { groupTrackingResults } from '../../utils/dashboardTracking';

function getDashboardName(module) {
    const reg = window.dashboardRegistry;
    if (!reg) return module;
    const [path, detail] = module.split(':');
    if (reg[path]) return reg[path].name + (detail ? ` — ${detail}` : '');
    return module;
}

function HistoryRow({ row }) {
    const [open, setOpen] = useState(false);
    const dashboardName = getDashboardName(row.module);

    return (
        <>
            <TableRow hover sx={{ '& > *': { borderBottom: 'unset' }, cursor: 'pointer' }} onClick={() => setOpen(!open)}>
                <TableCell padding="checkbox">
                    <IconButton size="small" onClick={e => { e.stopPropagation(); setOpen(!open); }}>
                        {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </TableCell>
                <TableCell>{row.date}</TableCell>
                <TableCell>
                    <Typography variant="body2" fontWeight={600}>{row.user}</Typography>
                </TableCell>
                <TableCell>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="body2">{dashboardName}</Typography>
                        <Tooltip title={`Open: #/${row.module}`} placement="right">
                            <IconButton size="small" onClick={e => { e.stopPropagation(); window.open(`#/${row.module}`, '_blank'); }}>
                                <OpenInNewIcon fontSize="inherit" />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                    <Typography variant="caption" color="text.secondary">{row.module}</Typography>
                </TableCell>
                <TableCell align="center">
                    <Chip label={row.visits.length} size="small" color="primary" variant="outlined" />
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 1, ml: 7 }}>
                            <Typography variant="subtitle2" gutterBottom>Visit timestamps</Typography>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>#</TableCell>
                                        <TableCell>Timestamp</TableCell>
                                        <TableCell>Detail</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {row.visits
                                        .slice()
                                        .sort((a, b) => b.datetime.localeCompare(a.datetime))
                                        .map((visit, idx) => (
                                            <TableRow key={idx}>
                                                <TableCell>{idx + 1}</TableCell>
                                                <TableCell>{moment(visit.datetime).format('YYYY-MM-DD HH:mm:ss')}</TableCell>
                                                <TableCell>
                                                    {visit.detail ? (
                                                        <Chip
                                                            label={visit.detail}
                                                            size="small"
                                                            variant="outlined"
                                                            sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}
                                                        />
                                                    ) : null}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                </TableBody>
                            </Table>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
}

export default function DashboardTrackingHistoryChart({ data }) {
    const results = data?.results ?? (Array.isArray(data) ? data : []);
    const groupedRows = useMemo(() => groupTrackingResults(results, true), [results]);

    if (!results.length) {
        return (
            <Typography variant="body2" color="text.secondary" p={2}>
                No records found.
            </Typography>
        );
    }

    return (
        <TableContainer component={Paper} variant="outlined">
            <Table size="small">
                <TableHead>
                    <TableRow sx={{ backgroundColor: '#f6f6f8' }}>
                        <TableCell padding="checkbox" />
                        <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>User</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Dashboard</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700 }}>Visits</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {groupedRows.map(row => <HistoryRow key={row.id} row={row} />)}
                </TableBody>
            </Table>
        </TableContainer>
    );
}